import { isFiniteNumber } from './narrativeFormatting';
import { getAuthoredOverview, renderAuthoredNarrativeBundle } from './authoredNarrativeRender';
import { buildVmtNarrativeTokens } from './vmtNarrativeTokens';
import { buildVmtStructuredMethodology } from './vmtMethodologyStructured';

export type VmtChartViewId = 'total' | 'percapita' | 'comparison';

export interface VmtNarrativeInput {
  locationName: string;
  viewId: VmtChartViewId;
  reportingYear: number;
  perCapitaMiPerDay: number | null;
  changeSince2019Pct: number | null;
  californiaPerCapitaMiPerDay: number | null;
  annualLdvVmt: number | null;
  changeSince2019AnnualPct: number | null;
  californiaAnnualLdvVmt: number | null;
  annualLdvSeries: { year: number; vmt: number }[];
}

const CHART_ID = 'vmt';

export function buildVmtAuthoredBundle(input: VmtNarrativeInput) {
  const tokens = buildVmtNarrativeTokens(input);
  const bundle = renderAuthoredNarrativeBundle(CHART_ID, input.viewId, tokens, {
    methodology: buildVmtStructuredMethodology(input.viewId),
  });
  bundle.methodology = buildVmtStructuredMethodology(input.viewId);
  return bundle;
}

export function buildVmtStructuredMethodologyForView(viewId: VmtChartViewId) {
  return buildVmtStructuredMethodology(viewId);
}

export function buildVmtOverviewDescription(viewId: VmtChartViewId): string {
  const fallback =
    viewId === 'comparison'
      ? 'Light-duty and heavy-duty vehicle miles traveled are shown separately.'
      : viewId === 'total'
        ? 'Annual light-duty vehicle miles traveled (passenger and on-demand auto).'
        : 'Daily light-duty VMT per resident.';
  return getAuthoredOverview(CHART_ID, viewId, fallback);
}

export function buildVmtKeyInsights(input: VmtNarrativeInput): string {
  const bundle = buildVmtAuthoredBundle(input);
  if (bundle.keyInsights) return bundle.keyInsights;
  const loc = input.locationName;
  if (input.viewId === 'total' && !isFiniteNumber(input.annualLdvVmt)) {
    return `Annual light-duty VMT is not available for ${loc} in the reporting year.`;
  }
  if (input.viewId === 'percapita' && !isFiniteNumber(input.perCapitaMiPerDay)) {
    return `Per-capita light-duty VMT is not available for ${loc}.`;
  }
  return '';
}

export function buildVmtOverviewBody(input: VmtNarrativeInput): string {
  return buildVmtKeyInsights(input);
}
